import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import PipelineForm from '../../components/Pipelines/PipelineForm';
import Spinner from '../../components/Spinner';
import { createFlowNode, generateEdgeId, getTypeOfFlow } from '../../constants/flowTypes';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const page = `${Page['pipelines']}`;

const PipelinesDetailsPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [pipeline, setPipeline] = useState();
  const { accountId, domainId, pipelineId } = useParams();

  const queryParams = new URLSearchParams(location.search);
  const flowType = queryParams.get('flowType');
  const flowElementId = parseInt(queryParams.get('flowElementId'));
  const flowElementName = queryParams.get('flowElementName');

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pipelines/${pipelineId}`)
      .then(({ pipeline }) => {
        if (flowElementId && flowType && flowElementName && pipeline) {
          const nodes = pipeline.flow.nodes;
          const edges = pipeline.flow.edges;
          const outgoingEdgesMap = new Map();
          for (const edge of edges) {
            const sourceNodeId = edge.source;
            if (!outgoingEdgesMap.has(sourceNodeId)) {
              outgoingEdgesMap.set(sourceNodeId, []);
            }
            outgoingEdgesMap.get(sourceNodeId).push(edge);
          }
          let lastNode = null;
          for (const node of nodes) {
            const nodeId = node.id;
            if (!outgoingEdgesMap.has(nodeId)) {
              lastNode = node;
            }
          }

          const newFlowNode = createFlowNode(flowType, flowElementId, flowElementName, {
            x: 276,
            y: lastNode.position.y + 200,
          });

          pipeline.flow.edges.push({
            id: generateEdgeId(lastNode.id, getTypeOfFlow(flowType) + flowElementId),
            source: lastNode.id,
            target: getTypeOfFlow(flowType) + flowElementId,
          });
          pipeline.flow.nodes.push(newFlowNode);
        }

        if (!pipeline.googleAssetGroup) {
          pipeline.googleAssetGroup = {};
        }

        setPipeline(pipeline);
      })
      .catch((error) => {
        setLoadError(error);
      });
  }, [fetch, accountId, domainId, pipelineId, flowElementId, flowType, flowElementName]);

  const update = (pipeline) => {
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pipelines/${pipelineId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...pipeline, running: undefined }),
    })
      .then(() => {
        navigate(location.pathname.replace(`/pipelines/${pipeline.id}/details`, `/pipelines`));
      })
      .catch(setUserError);
  };

  const del = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pipelines/${pipeline.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/pipelines/${pipeline.id}/details`, `/pipelines`));
      })
      .catch(setLoadError);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (pipeline === undefined) {
    return <Spinner />;
  }

  const enitityLabel = pipeline.name;

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/pipelines` },
    { title: enitityLabel },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <div data-tour="pipelineDetails">
      <Metadata title={`${page} | ${pipeline.name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <PipelineForm pipeline={pipeline} savePipeline={update} deletePipeline={(cb) => del(cb)} />
      {userError && (
        <div style={{ marginLeft: '50%' }}>
          <ErrorMessage message={userError.message} style={{ whiteSpace: 'pre-line', lineHeight: '1' }} />
        </div>
      )}
    </div>
  );
};

export default PipelinesDetailsPage;
